import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, VolumeX, Volume2, Settings } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { nativePushPermission, registerNativePush, openNotificationSettings } from "@/utils/nativePush";
import { usePhoneMuted } from "@/hooks/usePhoneMuted";

/* Carte « Alertes » du profil partenaire (app native uniquement) : état de la
   permission notifications + bouton vers les réglages du téléphone, et état
   silencieux/vibreur. Ne rend rien sur le web. */
export default function AlertReadinessCard() {
  const { t } = useTranslation();
  const [perm, setPerm] = useState(null);
  const muted = usePhoneMuted();

  useEffect(() => {
    let alive = true;
    const check = async () => {
      const p = await nativePushPermission();
      if (alive) setPerm(p);
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    check();
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  if (!Capacitor.isNativePlatform() || perm === null || perm === "unsupported") return null;
  const granted = perm === "granted";

  const enable = async () => {
    if (perm === "denied") { await openNotificationSettings(); return; }
    const res = await registerNativePush();
    setPerm(res === "granted" ? "granted" : "denied");
  };

  return (
    <div className="rounded-2xl border border-sand bg-white p-4 mb-6" data-testid="alert-readiness-card">
      <div className="text-xs uppercase tracking-[0.2em] text-brand-red font-semibold mb-3">{t("push.cardTitle")}</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          {granted ? <Bell className="w-5 h-5 text-brand-green" /> : <BellOff className="w-5 h-5 text-brand-red" />}
          <span className={granted ? "text-brand-green font-semibold" : "text-brand-red font-semibold"} data-testid="push-permission-status">
            {granted ? t("push.statusGranted") : t("push.statusDenied")}
          </span>
        </div>
        {!granted && (
          <button onClick={enable} data-testid="open-notification-settings-btn"
            className="rounded-full bg-ink text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-brand-red transition-colors">
            <Settings className="w-4 h-4" /> {t("push.openSettings")}
          </button>
        )}
      </div>
      {muted !== null && (
        <div className="flex items-center gap-2 text-sm mt-3 pt-3 border-t border-sand" data-testid="silent-mode-status">
          {muted ? <VolumeX className="w-5 h-5 text-amber-600" /> : <Volume2 className="w-5 h-5 text-brand-green" />}
          <span className={muted ? "text-amber-700 font-semibold" : "text-mute"}>
            {muted ? t("push.silentTitle") : t("push.soundOk")}
          </span>
        </div>
      )}
    </div>
  );
}
